import type { ReactNode } from "react";
import { BottomNav } from "./BottomNav";
import { SideNav, type NavId } from "./SideNav";

type TabId = "home" | "find" | "play" | "feed" | "me";

type Props = {
  /** Nav highlight — extended ids (tournaments/chat/club/leaderboard/achievements) fall back on mobile via MOBILE_FALLBACK. */
  active?: NavId;
  /** Page header — pass the page's <TopBar …/> element. */
  top?: ReactNode;
  /** Projector/board pages: lift the width cap. */
  wide?: boolean;
  /** Hide both navs (auth/onboarding flows). */
  bare?: boolean;
  children: ReactNode;
};

const MOBILE_FALLBACK: Partial<Record<NavId, TabId>> = {
  tournaments: "play",
  chat: "feed",
  club: "me",
  leaderboard: "me",
  achievements: "me",
};

/**
 * PageShell — responsive app frame.
 * Mobile (<lg): the original 480px column + BottomNav.
 * Desktop (lg+): SideNav rail + wide centered content, no BottomNav.
 */
export function PageShell({ active = "home", top, wide, bare, children }: Props) {
  const bottomActive: TabId = (
    ["home", "find", "play", "feed", "me"].includes(active)
      ? active
      : (MOBILE_FALLBACK[active] ?? "home")
  ) as TabId;

  return (
    <div className="lg:flex lg:min-h-[100dvh]">
      {!bare && <SideNav active={active} />}
      <div className="lg:flex-1 lg:min-w-0">
        <main
          className={`bm-grit min-h-[100dvh] flex flex-col mx-auto relative overflow-hidden w-full ${
            wide
              ? "max-w-none lg:px-4"
              : bare
                ? // bare = logged-out/auth/onboarding/offline screens — single-column
                  // hero content, deliberately excluded from the full-width treatment.
                  "max-w-[480px] lg:max-w-[1100px] xl:max-w-[1280px]"
                : "max-w-[480px] lg:max-w-none"
          }`}
        >
          {top}
          <div
            className={`flex-1 overflow-y-auto px-4 pt-3 lg:px-8 ${
              bare ? "xl:px-10" : "xl:px-12 2xl:px-16"
            } lg:pt-4 lg:pb-12 ${bare ? "pb-8" : "pb-24"}`}
          >
            {children}
          </div>
          {!bare && (
            <div className="lg:hidden">
              <BottomNav active={bottomActive} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
